import {
  Component,
  OnDestroy,
  OnInit,
  TemplateRef,
  ViewContainerRef,
} from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { Subject, takeUntil } from 'rxjs';
import {
  isLargeScreen,
  updateCustomTableHeaders,
} from '../../../../global.variable';
import { AppService } from '../../../app.service';
import { ConfirmDialogComponent } from '../../../shared/components/confirm-dialog/confirm-dialog.component';
import { ModalDialogueService } from '../../../shared/components/modal-dialogue/modal-dialogue.service';
import { DataCheckService } from '../../../shared/services/data-check.service';
import { LoadingSpinnerService } from '../../../shared/services/loading-spinner.service';
import { ToasterService } from '../../../shared/services/toaster.service';
import { CheckpointService } from '../checkpoints/checkpoint.service';
import { getDateTimeRange } from '../tableDetails';
import { UsersService } from '../users/users.service';

@Component({
  selector: 'gtapp-user-event-log',
  standalone: false,
  templateUrl: './user-event-log.component.html',
  styleUrl: './user-event-log.component.scss',
})
export class UserEventLogComponent implements OnInit, OnDestroy {
  largeView: Boolean = isLargeScreen;
  userEvents: any;
  paginatedResults: any = {};
  infiniteDataList: any = [];
  selectedEvent: any;
  loadingData = false;

  userEventDetailsMobileView: any = {};
  userEventDetailsTableView: any = [];
  tableStyle = {
    'overflow': 'auto',
    'max-height': '100%',
  };

  scrollPosition: any;

  //pagination components
  previous: number = 0;
  pageSize: number = 10;
  pageNum: number = 1;
  totalPages: number = 0;
  totalRows: number = 0;
  sortKeys: any = ['-updated_at'];
  eventLogDefaultDateRange = getDateTimeRange();
  selectedSubUserId: string = '';

  searchParams: any = {
    updated_at__range: [
      this.eventLogDefaultDateRange?.dateRange?.start,
      this.eventLogDefaultDateRange?.dateRange?.end,
    ],
  };

  subscriberUserId: any;
  newComment: any;
  previousComment: any;

  otherParams: any = {
    apiFiltering: true,
    apiCalled: false,
    tableRowStyle: (rowData: any) => {
      if (rowData?.is_deleted) {
        return {
          opacity: '0.6',
        };
      }
      return {};
    },
  };

  isAdmin = this.dataCheckService?.isUserAdmin();
  isDispatchUser = this.dataCheckService?.isDispatchUser();

  cardActionIcons: any = [
    {
      condition: (row: any) => {
        if (this.patrolEditDeleteRestoreCheck(row, 'edit')) {
          return {
            icon: 'fa-regular fa-pen-to-square md-font-size text-secondary ms-2 mb-3 ',
            confirm: true,
            type: 'edit',
          };
        }
        return {};
      },
    },
    {
      condition: (row: any) => {
        if (this.patrolEditDeleteRestoreCheck(row)) {
          return {
            icon: `fa-regular ${
              row?.is_deleted ? 'fa-square-plus' : 'fa-square-minus'
            } md-font-size text-secondary ms-2`,
            confirm: true,
            type: row?.is_deleted ? 'restore' : 'delete',
          };
        }
        return {};
      },
    },
  ];

  selectedSite: any = {};

  userEventLogSubject = new Subject<void>();

  constructor(
    private userService: UsersService,
    private spinnerService: LoadingSpinnerService,
    private route: ActivatedRoute,
    private appService: AppService,
    private checkpointService: CheckpointService,
    private router: Router,
    private dialogService: ModalDialogueService,
    private viewContainerRef: ViewContainerRef,
    private toasterService: ToasterService,
    private dataCheckService: DataCheckService,
  ) {
    let userData = this.appService.getUserData();
    this.subscriberUserId = userData?.subscriber?.subscriber_user_id;
    this.formatRouteUrl();

    this.getTableCardDetails();
  }
  ngOnDestroy(): void {
    this.userEventDetailsMobileView = {};
    this.userEventDetailsTableView = [];
  }
  ngOnInit(): void {
    if (this.route.snapshot.fragment || !this.largeView) {
      this.getUserUpdateLogEvents();
    } else {
      let attempts = 0;
      const maxAttempts = 5;
      const checkFilterInterval = setInterval(() => {
        const appliedSearchFilters = document.getElementById(
          'appliedSearchFilterId',
        );
        attempts++;
        if (appliedSearchFilters) {
          appliedSearchFilters.click();
          clearInterval(checkFilterInterval);
        } else if (attempts >= maxAttempts) {
          clearInterval(checkFilterInterval);
        }
      }, 100);
    }
  }
  patrolEditDeleteRestoreCheck(
    selectedEvent: any,
    action: string = 'restoreDelete',
  ) {
    if (action == 'edit') {
      return (
        this.subscriberUserId == selectedEvent?.subs_user_id &&
        !selectedEvent?.is_deleted &&
        selectedEvent?.company_id &&
        selectedEvent?.event_type === 'Patrol Update' &&
        [1, 2, 7].includes(selectedEvent?.event_action) &&
        new Date().getTime() - new Date(selectedEvent?.updated_at).getTime() <
          72 * 3600 * 1000
      );
    }

    return (
      (this.subscriberUserId == selectedEvent?.subs_user_id || this.isAdmin) &&
      selectedEvent?.company_id &&
      selectedEvent?.event_type === 'Patrol Update' &&
      [1, 2, 7].includes(selectedEvent?.event_action) &&
      new Date().getTime() - new Date(selectedEvent?.updated_at).getTime() <
        3600000
    );
  }
  getTotalEventCount(body: any, retries = 1) {
    this.userService
      .getUserUpdateLog(body, { get_count: 1 })
      .subscribe((response: any) => {
        if (response['status'] == 'success') {
          this.totalRows = response?.total_size;
          this.totalPages = Math.ceil(this.totalRows / this.pageSize);

          this.otherParams = {
            ...this.otherParams,
            apiFiltering: true,
            apiCalled: true,
            paginationData: {
              totalRows: this.totalRows,
              previousRows: this.previous,
              rows: this.pageSize,
              pageNum: this.pageNum,
              totalPages: this.totalPages,
            },
          };

          this.checkAndLoadNextBatch();
        } else {
          if (retries < 3) {
            retries += 1;
            this.getTotalEventCount(body, retries);
          }
        }
      });
  }
  getApiRequestBody() {
    let body: any = {
      event_type: 'user_update_history',
      search_filters: {},
    };

    if (this.selectedSite?.id)
      this.searchParams = {
        ...this.searchParams,
        site_id: this.selectedSite?.id,
      };
    if (!this.largeView && !this.selectedSubUserId) {
      this.selectedSubUserId = this.subscriberUserId;
    }
    if (this.selectedSubUserId) body.sub_user_id = this.selectedSubUserId;

    if (this.sortKeys) body.sort_key = this.sortKeys;
    if (this.searchParams) body.search_filters = this.searchParams;
    if (this.pageSize) body.rows = this.pageSize;
    if (this.previous) body.previous = this.previous;
    return body;
  }
  getUserUpdateLogEvents(getCount = true) {
    this.spinnerService.show();
    const body = this.getApiRequestBody();
    if (getCount) this.getTotalEventCount(body);

    this.userService.getUserUpdateLog(body).subscribe((response: any) => {
      if (response['status'] == 'success') {
        this.selectedEvent = null;

        this.userEvents = response?.data || [];
        this.paginatedResults[this.pageNum] = response?.data || [];
        this.updateRoutPath();
        let newList = response?.data || [];
        this.infiniteDataList = this.infiniteDataList.concat(newList);
        this.checkAndLoadNextBatch();
        this.spinnerService.hide();
      } else {
        this.toasterService.setMessage({
          successMessage: '',
          errorMessage: response['message'],
        });
        this.spinnerService.hide();
      }
    });
  }

  checkAndLoadNextBatch() {
    if (
      this.pageNum < this.totalPages &&
      !this.paginatedResults[this.pageNum + 1] &&
      !this.loadingData
    ) {
      this.loadingData = true;
      let body = this.getApiRequestBody();
      body.previous = (body?.previous || 0) + this.pageSize;
      // implies there is one more page to load
      this.userService
        .getUserUpdateLog(body)
        .pipe(takeUntil(this.userEventLogSubject))
        .subscribe((response: any) => {
          if (response['status'] == 'success') {
            this.paginatedResults[this.pageNum + 1] = response?.data || [];
            this.loadingData = false;
          }
        });
    }
  }
  updateRoutPath() {
    this.router.navigate([], {
      fragment: btoa(
        JSON.stringify({
          searchParams: this.searchParams,
          selectedSite: this.selectedSite,
          otherParams: this.otherParams,
        }),
      ),
      queryParams: {},
      replaceUrl: true, // Replaces the current URL without adding a new history entry
    });
  }
  clearPagination() {
    this.paginatedResults = {};
    this.previous = 0;
    this.pageSize = 10;
    this.pageNum = 1;
    this.totalPages = 0;
    this.totalRows = 0;
    this.infiniteDataList = [];
  }
  onTableRowClick(event: any) {
    if (event?.id == this.selectedEvent?.id) return;
    this.scrollPosition = window.scrollY;
    this.selectedEvent = { ...event };
    this.tableStyle = {
      'overflow': 'auto',
      'max-height': '35vh',
    };
    if (event?.id && event?.event_type) {
      // call backend to get detailed info if its event log
      let body: any = {
        event_type: 'user_update_history_detail',
        update_type: event?.event_type,
        event_id: btoa(event?.id),
      };
      this.userService.getUserUpdateLog(body).subscribe((response: any) => {
        if (response['status'] == 'success') {
          this.selectedEvent = { ...event, ...response?.data };
        } else {
          this.toasterService.setMessage({
            successMessage: '',
            errorMessage: response['message'],
          });
        }
      });
    }
  }
  onCardActionIconClick(event: any, requestRef: TemplateRef<any>) {
    if (event?.type === 'edit') {
      this.editComment(event?.event, requestRef);
    } else {
      this.deleteRestoreUserUpdate(event);
    }
  }

  sortColumn(body: any) {
    this.clearPagination();
    this.sortKeys = body?.sortList || [];
    this.searchParams = body?.searchParams || {};
    this.selectedSubUserId = body?.sub_user_id || '';
    this.getUserUpdateLogEvents();
  }
  searchColumn(event: any) {
    this.clearPagination();
    this.searchParams = event?.searchParams || {};
    this.selectedSubUserId = event?.sub_user_id || '';
    this.getUserUpdateLogEvents();
  }
  hideDetails() {
    this.selectedEvent = null;
    // let scrollPosition: any = window.localStorage.getItem('scrollToView');
    setTimeout(() => {
      window.scrollTo(0, this.scrollPosition);
    }, 100);
  }

  editComment(commentData: any, requestRef: TemplateRef<any>) {
    this.newComment = commentData?.detail;
    this.previousComment = commentData?.detail;
    this.dialogService.open(
      requestRef,
      {
        data: commentData,
      },
      this.viewContainerRef,
    );
  }
  addComment(data: any) {
    if (!data?.id && !this.newComment?.trim()) {
      this.toasterService.setMessage({
        successMessage: '',
        errorMessage: 'Comment cannot be empty',
      });
      return;
    }

    this.spinnerService.show();
    let body = {
      company_id: data?.company_id,
      comment: this.newComment,
      id: data?.id,
      edit_comment: 1,
    };
    this.checkpointService.updateClient(body).then((response: any) => {
      this.getUserUpdateLogEvents(false);
      this.newComment = '';
      this.selectedEvent = null;
      this.spinnerService.hide();
      window.location.reload();
    });
  }
  loadMoreMobileData() {
    this.previous = this.previous + this.pageSize;
    this.getUserUpdateLogEvents(false);
  }

  onPaginationChange(data: any) {
    window.scrollTo({
      top: 0,
      behavior: 'smooth',
    });

    this.previous = data?.paginationData?.previousRows;
    this.pageNum = data?.paginationData?.pageNum;

    if (data?.paginationData?.rows == this.pageSize) {
      this.pageSize = data?.paginationData?.rows;
      // do preloading only here
      if (this.paginatedResults?.[this.pageNum]) {
        this.otherParams.paginationData = {
          totalRows: this.totalRows,
          previousRows: this.previous,
          rows: this.pageSize,
          pageNum: this.pageNum,
          totalPages: Math.ceil(this.totalRows / this.pageSize),
        };

        this.userEvents = this.paginatedResults?.[this.pageNum];
        this.updateRoutPath();

        this.totalPages = Math.ceil(this.totalRows / this.pageSize);
        const nextPage = this.pageNum + 1;
        if (!this.paginatedResults?.[nextPage]) this.checkAndLoadNextBatch();
      } else {
        this.loadingData = false;
        this.userEventLogSubject?.next();
        this.getUserUpdateLogEvents(false);
        this.otherParams.paginationData = { ...data?.paginationData };
      }
    } else {
      this.paginatedResults = {};
      this.loadingData = false;
      this.userEventLogSubject?.next();
      this.pageSize = data?.paginationData?.rows;
      this.getUserUpdateLogEvents(false);
      this.otherParams.paginationData = { ...data?.paginationData };
    }
  }
  clearAndFocusTextBox(elementId: string) {
    this.newComment = '';
    setTimeout(() => {
      var element = <HTMLInputElement>document.getElementById(elementId);
      element?.focus();
    }, 100);
  }
  deleteRestoreUserUpdate(event: any) {
    const dialogRef = this.dialogService.open(ConfirmDialogComponent, {
      data: {
        title: event?.type === 'delete' ? 'Delete Event' : 'Restore Event',
        message:
          event?.type === 'delete'
            ? `Are you sure you want to delete this event?`
            : 'Are you sure you want to restore this event?',
      },
    });
    dialogRef.afterClosed().subscribe((value: any) => {
      if (value === true) {
        this.spinnerService.show();
        let body: any = {
          id: event?.event?.id,
        };
        body[event?.type === 'delete' ? 'delete_update' : 'restore_update'] = 1;
        this.checkpointService.updateClient(body).then((response: any) => {
          if (response?.status == 'success') {
            window.location.reload();
            this.selectedEvent = null;
            this.toasterService.setMessage({
              successMessage: response?.message,
              errorMessage: '',
            });
          } else {
            this.spinnerService.hide();
            this.toasterService.setMessage({
              successMessage: '',
              errorMessage: response?.message,
            });
          }
        });
      }
    });
  }

  formatRouteUrl() {
    if (!this.route.snapshot.fragment) return;
    try {
      const urlFragmentDetails = JSON.parse(atob(this.route.snapshot.fragment));
      if (urlFragmentDetails && Object.keys(urlFragmentDetails)?.length) {
        this.searchParams = {
          ...this.searchParams,
          ...(urlFragmentDetails?.searchParams || {}),
        };
        if (urlFragmentDetails?.otherParams?.paginationData) {
          this.previous =
            urlFragmentDetails?.otherParams?.paginationData?.previousRows ||
            this.previous;
          this.pageNum =
            urlFragmentDetails?.otherParams?.paginationData?.pageNum ||
            this.pageNum;
          this.pageSize =
            urlFragmentDetails?.otherParams?.paginationData?.rows ||
            this.pageSize;
        }

        this.selectedSite = urlFragmentDetails?.selectedSite || {};
      }
    } catch (error) {}
  }
  getTableCardDetails() {
    let individualDetails = [
      {
        name: 'user_name',
        header: 'Guard',
        // searchKey: 'user_name',
        // autoComplete: 'full_name',
        cardRowStyle: {
          'float': 'right',
          'font-size': 'small',
        },
      },
      {
        name: 'updated_at',
        datetimeObj: true,
        permanent: true,
        dateRangeKey: 'updated_at',
        ...this.eventLogDefaultDateRange,
        sortKey: 'updated_at',
        cardRowStyle: {
          'font-size': 'small',
        },
      },

      {
        name: 'action',
        suffixNestedValue: (row: any) => {
          return `[${row?.event_type}]`;
        },
        suffixStyle: { 'font-size': 'small', 'font-weight': 'bold' },
      },
      {
        nestedValue: (row: any) => {
          return row?.detail?.length > 150
            ? `${row?.detail?.slice(0, 150)}...`
            : row?.detail;
        },
        cardRowStyle: {
          'font-size': 'small',
          'white-space': 'pre-line',
          'color': 'var(--bs-secondary)',
          'margin': '0.5rem 0',
          'display': 'flex',
        },
      },
      {
        name: 'event_key',
        cardRowStyle: { 'font-size': 'small' },
      },

      {
        name: 'site_name',
        suffixNestedValue: (row: any) => {
          return row?.company_name && row?.site_name
            ? `[${row?.company_name}]`
            : row?.company_name;
        },
        cardRowStyle: {
          'font-weight': 'bold',
        },
        suffixStyle: {
          'font-size': 'small',
          'color': 'var(--bs-secondary)',
        },
      },
    ];

    let userEventDetailsTableView = [
      {
        header: 'Event Time',
        name: 'updated_at',
        datetimeObj: true,
        dateRangeKey: 'updated_at',
        sortKey: 'updated_at',
        permanent: true,
        ...this.eventLogDefaultDateRange,
        default: true,
      },
      {
        header: 'Event Type',
        name: 'event_type',
        dropdownKey: 'event_type_id',
        multipleSelect: true,
        dropDownList: () => {
          return [
            { id: 1, name: 'Patrol Updates' },
            { id: 2, name: 'Job Updates' },
            { id: 3, name: 'Incident Updates' },
            { id: 4, name: 'User Updates' },
            { id: 5, name: 'Scheduled Patrol Updates' },
          ];
        },
      },
      {
        header: 'Event Action',
        name: 'action',
      },
      {
        header: 'Update',
        name: 'detail',
      },
      {
        header: 'Site/Client',
        name: 'site_name',
        searchKey: this.selectedSite?.id ? false : 'site_name',
        columnDataStyle: {
          'white-space': 'pre-line',
        },

        nestedValue: (row: any) => {
          let appendList = [];
          if (row?.event_key) appendList.push(row?.event_key);
          if (row?.site_name) appendList.push(row?.site_name);
          if (row?.company_name) appendList.push(row?.company_name);
          return appendList.join('\n');
        },
      },
      {
        header: 'User',
        name: 'user_name',
        searchKey: 'user_name',
        autoComplete: 'user',
        isGuardColumn: true,
        isFilterRequired: true,
      },
    ];

    if (!(this.isAdmin || this.isDispatchUser)) {
      individualDetails.splice(0, 1);
      userEventDetailsTableView.splice(
        userEventDetailsTableView?.length - 1,
        1,
      );
    }
    if (this.selectedSite?.id) {
      individualDetails = individualDetails?.filter(
        (item: any) => item?.name !== 'site_name',
      );
      userEventDetailsTableView = userEventDetailsTableView?.map((item: any) =>
        item?.name === 'site_name'
          ? { header: 'Event', name: 'event_key' }
          : item,
      );
    }
    updateCustomTableHeaders(individualDetails, this.searchParams);

    this.userEventDetailsMobileView = {
      individualDetails: [...individualDetails],
      groupDetails: (row: any) => {
        if (row?.is_deleted) {
          return {
            opacity: '0.6',
          };
        }

        return {};
      },
    };

    updateCustomTableHeaders(userEventDetailsTableView, this.searchParams);

    this.userEventDetailsTableView = [...userEventDetailsTableView];
  }
}
